<template>
  <div class="questionnaire-container questionnaire-repeat-group row visible-on col-sm-10 col-sm-offset-1 col-md-10 col-md-offset-1 col-lg-8 col-lg-offset-2">
    <div ref="form-label" :class="['col-xs-12 form-label text-left', fixStyle]">{{ label }}</div>
    <div class="repeat-group-body col-xs-12">
      <div v-for="(row, rowIndex) in internalRows" :key="row._vid" class="repeat-row">
        <div class="repeat-row-header">
          <span>{{ $t('questionnaire.label.entryNo', { number: rowIndex + 1 }) }}</span>
          <button v-if="canRemove" type="button" @click="removeRow(rowIndex)">{{ $t('questionnaire.btn.remove') }}</button>
        </div>
        <div class="repeat-fields">
          <div
            v-for="field in subFields"
            :key="field.key"
            class="repeat-field"
            :class="{ 'repeat-field--date-range': field.type === 'DATE_RANGE' }"
            :style="{ width: field.width || null }"
          >
            <label>{{ getConfigText(field, 'label') }}</label>
            <template v-if="field.type === 'DATE_RANGE'">
              <div class="date-range-field">
                <el-date-picker
                  :disabled="isDisabled"
                  :clearable="false"
                  :value="getDateRangeValue(row, field.key, 'start')"
                  type="month"
                  value-format="yyyy-MM"
                  format="yyyy-MM"
                  popper-class="questionnaire-month-picker"
                  :placeholder="getDateRangePlaceholder(field, 'start')"
                  @input="onDateRangeChange(rowIndex, field.key, 'start', $event)"
                />
                <span class="range-separator">-</span>
                <el-date-picker
                  :disabled="isDisabled"
                  :value="getDateRangeValue(row, field.key, 'end')"
                  type="month"
                  value-format="yyyy-MM"
                  format="yyyy-MM"
                  popper-class="questionnaire-month-picker"
                  :placeholder="getDateRangePlaceholder(field, 'end')"
                  @input="onDateRangeChange(rowIndex, field.key, 'end', $event)"
                />
              </div>
            </template>
            <input
              v-else
              type="text"
              class="field-value"
              :disabled="isDisabled"
              :value="row[field.key]"
              :placeholder="getConfigText(field, 'placeholder')"
              @input="onTextChange(rowIndex, field.key, $event)"
            >
          </div>
        </div>
      </div>
      <button v-if="canAdd" type="button" class="add-row-btn" @click="addRow">{{ addButtonText }}</button>
    </div>
    <div v-if="internalErrorMessage" class="col-xs-12 error-text">{{ internalErrorMessage }}</div>
  </div>
</template>

<script>
import _ from 'lodash'
import { questionnaireMixin } from './mixin.js'
import { deviceType } from '@/utils/common'

export default {
  mixins: [questionnaireMixin],
  props: {
    label: {
      type: String,
      default: ''
    },
    isEditable: {
      type: String,
      default: ''
    },
    value: {
      type: [String, Object],
      default: ''
    },
    isRequired: {
      type: Boolean,
      default: false
    },
    config: {
      type: Object,
      default: null
    }
  },
  data () {
    return {
      isMobile: deviceType.isMobile,
      isDisabled: this.isEditable === 'false',
      internalRows: [],
      nextRowVid: 1,
      internalErrorMessage: ''
    }
  },
  model: {
    prop: 'value',
    event: 'change'
  },
  computed: {
    subFields () {
      return (this.config && this.config.sub_fields) || []
    },
    minRows () {
      return this.config && this.config.min_rows !== undefined ? Number(this.config.min_rows) : 0
    },
    maxRows () {
      return this.config && this.config.max_rows !== undefined ? Number(this.config.max_rows) : 10
    },
    canAdd () {
      return !this.isDisabled && this.internalRows.length < this.maxRows
    },
    canRemove () {
      return !this.isDisabled && this.internalRows.length > this.minRows
    },
    addButtonText () {
      return this.getConfigText(this.config || {}, 'add_button_text') || this.$t('questionnaire.btn.addEntry')
    }
  },
  created () {
    this.resetRows(this.value)
    if (this.internalRows.length < this.minRows) {
      for (let index = this.internalRows.length; index < this.minRows; index++) {
        this.internalRows.push(this.createEmptyRow())
      }
    }
  },
  watch: {
    value: {
      handler (val) {
        this.resetRows(val)
      },
      immediate: true
    },
    internalRows: {
      handler () {
        // The backend stores repeat group answers as {"rows": [...]} JSON in QuestionnaireAnswer.value.
        this.$emit('change', JSON.stringify({ rows: this.serializeRows() }))
      },
      deep: true
    },
    '$i18n.locale' () {
      this.validate()
    }
  },
  methods: {
    resetRows (value) {
      if (!value) {
        this.internalRows = []
        return
      }
      if (typeof value === 'string') {
        try {
          value = JSON.parse(value)
        } catch (e) {
          value = {}
        }
      }
      const rows = value.rows || []
      this.internalRows = _.map(rows, row => {
        return {
          ...row,
          _vid: this.nextRowVid++
        }
      })
    },
    serializeRows () {
      return _.chain(this.internalRows)
        .reject(row => this.isEmptyRow(row))
        .map(row => {
          const result = {}
          _.forEach(this.subFields, field => {
            result[field.key] = row[field.key]
          })
          return result
        })
        .value()
    },
    createEmptyRow () {
      const row = { _vid: this.nextRowVid++ }
      _.forEach(this.subFields, field => {
        if (field.type === 'DATE_RANGE') {
          row[field.key] = { start: '', end: null }
        } else {
          row[field.key] = ''
        }
      })
      return row
    },
    addRow () {
      if (!this.canAdd) {
        return
      }
      this.internalRows.push(this.createEmptyRow())
      this.internalErrorMessage = ''
    },
    removeRow (index) {
      if (!this.canRemove) {
        return
      }
      this.internalRows.splice(index, 1)
    },
    onTextChange (rowIndex, key, event) {
      this.$set(this.internalRows[rowIndex], key, event.target.value)
      this.internalErrorMessage = ''
    },
    onDateRangeChange (rowIndex, key, rangeKey, value) {
      const currentValue = this.internalRows[rowIndex][key] || { start: '', end: null }
      if (rangeKey === 'end' && !value) {
        currentValue.start = ''
        currentValue.end = null
      } else {
        currentValue[rangeKey] = rangeKey === 'end' ? (value || null) : (value || '')
      }
      this.$set(this.internalRows[rowIndex], key, currentValue)
      this.internalErrorMessage = ''
    },
    getDateRangeValue (row, key, rangeKey) {
      return row[key] && row[key][rangeKey] ? row[key][rangeKey] : ''
    },
    getConfigText (config, prefix) {
      let locale = this.$i18n.locale
      if (locale === 'zh') {
        locale = 'cn'
      } else if (locale === 'hk' || locale === 'tw') {
        locale = 'tc'
      }
      return config[`${prefix}_${locale}`] || config[`${prefix}_en`] || config[`${prefix}_cn`] || ''
    },
    getDateRangePlaceholder (field, key) {
      const placeholder = this.getConfigText(field, 'placeholder')
      if (placeholder) {
        return placeholder
      }
      return key === 'start' ? this.$t('questionnaire.label.startDate') : this.$t('questionnaire.label.endDate')
    },
    isEmptyRow (row) {
      return _.every(this.subFields, field => {
        const value = row[field.key]
        if (field.type === 'DATE_RANGE') {
          return !value || !value.start
        }
        return !value
      })
    },
    validate () {
      if (this.fieldIsRequired(this.isRequired) && this.internalRows.length === 0) {
        this.internalErrorMessage = this.isRequiredMessage
        return false
      }
      if (this.internalRows.length < this.minRows) {
        this.internalErrorMessage = this.$t('questionnaire.error.minRows', { count: this.minRows })
        return false
      }
      let valid = true
      const hasRequiredSubFields = _.some(this.subFields, 'required')

      if (hasRequiredSubFields) {
        // Gate 3-A: required sub-fields exist — validate them per row
        const rowsToCheck = this.fieldIsRequired(this.isRequired)
          ? this.internalRows
          : _.filter(this.internalRows, row => !this.isEmptyRow(row))
        _.forEach(rowsToCheck, row => {
          _.forEach(this.subFields, field => {
            if (!field.required) {
              return
            }
            const value = row[field.key]
            if (field.type === 'DATE_RANGE') {
              if (!value || !value.start) {
                valid = false
              }
            } else if (!value) {
              valid = false
            }
          })
        })
      } else if (this.fieldIsRequired(this.isRequired)) {
        // Gate 3-B: no required sub-fields but group is required — every row must have at least one non-empty field
        _.forEach(this.internalRows, row => {
          if (this.isEmptyRow(row)) {
            valid = false
          }
        })
      }
      // Gate 3-C: no required sub-fields + group not required — nothing to check

      this.internalErrorMessage = valid ? '' : (this.isRequiredMessage || this.$t('questionnaire.error.isRequired'))
      return valid
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/onboarding-common.scss';
@import '@/assets/scss/questionnaire-common.scss';

.questionnaire-repeat-group {
  margin-bottom: 1.2rem;

  .text-left {
    justify-content: flex-start;
  }

  .repeat-group-body {
    padding: 0;
    .repeat-row {
      border: 1px solid #d9d9d9;
      padding: 12px 14px;
      margin-bottom: 12px;
      background: #ffffff;
      .repeat-row-header {
        font-size: 14px;
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 10px;
        font-weight: 600;

        button {
          border: 0;
          background: transparent;
          color: #0096cc;
        }
      }

      .repeat-fields {
        display: flex;
        flex-wrap: wrap;
        margin: -5px -6px;
        align-items: end;

        .repeat-field {
          flex: 1;
          padding: 5px 6px;
          box-sizing: border-box;

          label {
            display: block;
            margin-bottom: 4px;
            font-size: 12px;
            line-height: 18px;
            color: #555555;
            overflow-wrap: break-word;
          }

          .field-value {
            font-size: medium;
          }

          > input,
          .date-range-field {
            width: 100%;
            border: 1px solid gray;
            padding: 0 8px;
            box-sizing: border-box;
          }

          > input {
            height: 38px;
            outline: none;
          }

          .date-range-field {
            display: flex;
            align-items: center;
            flex-wrap: nowrap;
            min-width: 260px;
            max-width: 100%;
            min-height: 38px;
            padding: 0;

            ::v-deep .el-date-editor.el-input {
              width: 100%;
            }

            ::v-deep .el-input__inner {
              height: 36px;
              line-height: 36px;
              border: 0;
              border-radius: 0;
              font-size: 14px;
            }

            ::v-deep .el-input__icon {
              line-height: 36px;
            }

            ::v-deep .el-date-editor:first-child {
              flex: 1 1 0;
              min-width: 0;

              .el-input__inner {
                padding-left: 52px;
                padding-right: 0;
              }
            }

            ::v-deep .el-date-editor:last-child {
              flex: 1 1 0;
              min-width: 0;

              .el-input__prefix {
                display: none;
              }

              .el-input__inner {
                padding-left: 24px;
                padding-right: 28px;
              }
            }

            .range-separator {
              display: inline-flex;
              flex: 0 0 40px;
              justify-content: center;
              color: #303133;
              font-size: 14px;
            }
          }
        }
        .repeat-field--date-range {
          min-width: 280px;

          .date-range-field {
            min-width: 0;
            flex-wrap: nowrap;

            ::v-deep .el-date-editor.el-input {
              width: auto;
              min-width: 0;
              flex: 1 1 0;
            }
          }
        }
      }
    }
    .add-row-btn {
      height: 36px;
      padding: 0 16px;
      border: 1px solid #0096cc;
      background: #ffffff;
      color: #0096cc;
    }
  }
}

@media (min-width: 769px) and (max-width: 1280px) {
  .questionnaire-repeat-group {
    .repeat-group-body {
      .repeat-row {
        .repeat-fields {
          .repeat-field {
            flex: 1 1 calc(50% - 12px);
            max-width: calc(50% - 12px);
          }

          .repeat-field--date-range {
            flex-basis: calc(50% - 12px);
          }
        }
      }
    }
  }
}

@media (max-width: 768px) {
  .questionnaire-repeat-group {
    .repeat-group-body {
      .repeat-row {
        padding: 10px 12px;
        .repeat-fields {
          flex-direction: column;
          margin: 0;
          .repeat-field {
            min-width: 100%;
            width: 100% !important;
            flex: none;
            padding: 4px 0;
            .date-range-field {
              flex-wrap: nowrap;
              min-width: 0;

              ::v-deep .el-date-editor.el-input {
                width: auto;
                min-width: 0;
                flex: 1 1 0;
              }
            }
          }
        }
        .repeat-row-header {
          flex-wrap: wrap;
        }
      }
    }
  }
}
</style>
